// v49 RRHH — /app/rrhh/catalogos
// Tres catalogos del modulo: tipos documentales (tabla propia, con bloque y
// bandera de vencimiento), listas rrhh_* (ListasConfiguracion) y los cargos
// aprobadores (Configuracion, D6).
//
// Gate: el modulo entero es del grupo RRHH. Cambiar los cargos aprobadores
// exige ADEMAS ser administrador (§6.6) — el backend responde 403 si no lo es.

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config/api';
import { useAdmin } from '../context/AdminContext';
import { LISTAS_RRHH, msgError, inputStyle, btnPrimario, btnSecundario, btnNavy, tarjeta, thStyle, tdStyle } from '../utils/rrhh';
import { Campo, Badge, Aviso } from '../components/RrhhCampos';

const SECCIONES = ['Tipos documentales', 'Listas', 'Cargos aprobadores'];

// Etiqueta visible de cada lista. La CLAVE nunca cambia (spec §3.1): renombrar
// la clave rompe los datos ya sembrados sin ganar nada, asi que los renombres
// pedidos por JC viven aca y solo aca.
const NOMBRE_LISTA = {
  rrhh_puesto: 'Puestos',
  rrhh_departamento: 'Departamentos',
  rrhh_jornada: 'Jornada Laboral',
  rrhh_modalidad_trabajo: 'Modalidad de trabajo',
  rrhh_estado_civil: 'Estado civil',
  rrhh_banco: 'Bancos',
  rrhh_parentesco: 'Parentesco',
  rrhh_periodicidad_pago: 'Modalidad de pago',
  rrhh_estado_activo: 'Activos entregados',
  rrhh_colegio_profesional: 'Colegio profesional',
  rrhh_grado_academico: 'Grado académico',
  rrhh_tipo_licencia: 'Tipo de licencia',
  rrhh_idioma: 'Idioma',
  rrhh_nivel_idioma: 'Nivel de idioma',
  rrhh_motivo_salida: 'Motivo de salida'
};

// Listas cuyos valores el backend reconoce por su TEXTO, o donde el campo
// 'orden' no es solo presentacion. Renombrar uno de estos valores desactiva la
// regla asociada sin que nada avise, asi que la pantalla lo advierte.
const NOTA_LISTA = {
  rrhh_grado_academico: 'El orden define la jerarquía, no solo cómo se ve la lista: el último grado académico del expediente sale del título con el número más alto. Al agregar un grado, escribí la posición que le corresponde en la escala y el resto baja un lugar solo. Si dejás el orden vacío queda de último, o sea pasa a ser el grado más alto.',
  rrhh_tipo_licencia: 'La alerta de vencimiento solo aplica a quien tenga una licencia tipo B.',
  rrhh_modalidad_trabajo: 'El sistema pide la dirección de teletrabajo cuando la modalidad se llama exactamente "Teletrabajo" o "Híbrida". Si renombrás esos dos valores o agregás otro equivalente con otro nombre, deja de pedirla.'
};

export default function RrhhCatalogos() {
  const [seccion, setSeccion] = useState(SECCIONES[0]);
  const [error, setError]     = useState('');
  const [aviso, setAviso]     = useState('');

  const comun = { setError, setAviso };

  return (
    <div style={{ padding: '24px', fontFamily: 'Segoe UI, sans-serif', maxWidth: '1100px' }}>
      <div style={{ background: '#012E40', borderRadius: '12px', padding: '16px 20px', marginBottom: '20px' }}>
        <span style={{ color: '#fff', fontSize: '17px', fontWeight: '700' }}>Catalogos de RRHH</span>
      </div>

      <div style={{ display: 'flex', gap: '2px', marginBottom: '16px', borderBottom: '1px solid #e0e0e0' }}>
        {SECCIONES.map(s => (
          <button key={s} onClick={() => { setSeccion(s); setError(''); setAviso(''); }}
            style={{
              padding: '9px 16px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: '13px', fontWeight: seccion === s ? '700' : '500',
              color: seccion === s ? '#012E40' : '#888',
              borderBottom: `2px solid ${seccion === s ? '#87B749' : 'transparent'}`, marginBottom: '-1px'
            }}>
            {s}
          </button>
        ))}
      </div>

      <Aviso texto={error} />
      <Aviso texto={aviso} tipo="ok" />

      {seccion === 'Tipos documentales' && <TiposDocumento {...comun} />}
      {seccion === 'Listas'             && <Listas {...comun} />}
      {seccion === 'Cargos aprobadores' && <Aprobadores {...comun} />}
    </div>
  );
}

// ── Tipos documentales (RrhhTiposDocumento) ─────────────────────────────
function TiposDocumento({ setError, setAviso }) {
  const [filas, setFilas] = useState([]);
  const [form, setForm]   = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await axios.get(`${API}/rrhh-catalogos?recurso=tipos-documento`);
      setFilas(r.data || []);
    } catch (e) { setError(msgError(e)); }
    setCargando(false);
  }, [setError]);

  useEffect(() => { cargar(); }, [cargar]);

  const nuevo  = () => setForm({ bloque: 'Personal', nombre: '', vence: false, orden: 0 });
  const editar = (t) => setForm({ id: t.id, bloque: t.bloque, nombre: t.nombre, vence: !!t.vence, orden: t.orden });

  const submit = async () => {
    if (!form.nombre?.trim()) { setError('El nombre es obligatorio.'); return; }
    setGuardando(true); setError(''); setAviso('');
    try {
      if (form.id) await axios.put(`${API}/rrhh-catalogos?recurso=tipos-documento&id=${form.id}`, form);
      else         await axios.post(`${API}/rrhh-catalogos?recurso=tipos-documento`, form);
      setForm(null);
      setAviso('Tipo documental guardado.');
      await cargar();
    } catch (e) { setError(msgError(e)); }
    finally { setGuardando(false); }
  };

  const desactivar = async (t) => {
    if (!window.confirm(`Desactivar "${t.nombre}"? Los documentos ya subidos con ese tipo no se tocan.`)) return;
    try { await axios.delete(`${API}/rrhh-catalogos?recurso=tipos-documento&id=${t.id}`); await cargar(); }
    catch (e) { setError(msgError(e)); }
  };

  return (
    <div style={{ ...tarjeta, padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
        <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#012E40' }}>Tipos documentales</h3>
        <button onClick={nuevo} style={btnPrimario}>+ Nuevo tipo</button>
      </div>

      {form && (
        <div style={{ marginBottom: 16, padding: 14, background: '#fafbfc', border: '1px solid #eee', borderRadius: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, alignItems: 'end' }}>
            <Campo label="Bloque" opciones={[{ id: 'Personal', valor: 'Personal' }, { id: 'Administrativo', valor: 'Administrativo' }]}
              valor={form.bloque} onChange={v => setForm(p => ({ ...p, bloque: v }))}
              deshabilitado={!!form.id} />
            <Campo label="Nombre" requerido valor={form.nombre} onChange={v => setForm(p => ({ ...p, nombre: v }))} />
            <Campo label="Orden" tipo="number" valor={form.orden} onChange={v => setForm(p => ({ ...p, orden: v }))} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#555', paddingBottom: 8 }}>
              <input type="checkbox" checked={form.vence} onChange={e => setForm(p => ({ ...p, vence: e.target.checked }))} />
              Vence
            </label>
          </div>
          {form.id && <div style={{ fontSize: 11, color: '#888', marginTop: 8 }}>El bloque no se cambia al editar: define donde se agrupa el documento en el expediente.</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <button onClick={() => setForm(null)} style={btnSecundario}>Cancelar</button>
            <button onClick={submit} disabled={guardando} style={{ ...btnNavy, opacity: guardando ? 0.7 : 1 }}>
              {guardando ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      )}

      {cargando ? <div style={{ padding: 30, textAlign: 'center', color: '#888' }}>Cargando...</div>
        : ['Personal', 'Administrativo'].map(bloque => {
          const delBloque = filas.filter(t => t.bloque === bloque);
          return (
            <div key={bloque} style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                Expediente {bloque} ({delBloque.length})
              </div>
              {delBloque.length === 0
                ? <div style={{ fontSize: 12, color: '#bbb' }}>Sin tipos en este bloque.</div>
                : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead><tr style={{ background: '#f8f9fa' }}>
                      {['Orden', 'Nombre', 'Vence', ''].map(h => <th key={h} style={thStyle}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {delBloque.map(t => (
                        <tr key={t.id} style={{ borderBottom: '1px solid #f3f3f3' }}>
                          <td style={tdStyle}>{t.orden}</td>
                          <td style={{ ...tdStyle, color: '#012E40', fontWeight: 500 }}>{t.nombre}</td>
                          <td style={tdStyle}>{t.vence ? <Badge texto="Vence" tono="ambar" /> : '—'}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>
                            <button onClick={() => editar(t)} style={miniBtn}>Editar</button>
                            <button onClick={() => desactivar(t)} style={{ ...miniBtn, color: '#A32D2D', borderColor: '#fcc', marginLeft: 6 }}>Desactivar</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
            </div>
          );
        })}
    </div>
  );
}

// ── Listas rrhh_* (ListasConfiguracion, solo-RRHH) ──────────────────────
function Listas({ setError, setAviso }) {
  const [todas, setTodas]   = useState({});
  const [activa, setActiva] = useState(LISTAS_RRHH[0]);
  const [nuevo, setNuevo]   = useState('');
  const [nuevoOrden, setNuevoOrden] = useState('');
  const [editando, setEditando] = useState(null);   // {id, valor, orden}
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await axios.get(`${API}/listas`);
      setTodas(r.data || {});
    } catch (e) { setError(msgError(e)); }
    setCargando(false);
  }, [setError]);

  useEffect(() => { cargar(); }, [cargar]);

  const valores = todas[activa] || [];

  // El orden es editable a proposito: en listas como Grado academico define la
  // jerarquia, no solo como se ordena la pantalla.
  //
  // Escribir una posicion ocupada INSERTA ahi y empuja al resto un lugar hacia
  // abajo — lo resuelve el backend en una transaccion, no a punta de PUTs desde
  // aca. Vacio = siguiente consecutivo, que es lo razonable donde el orden solo
  // decide como se ve la lista.
  const agregar = async () => {
    if (!nuevo.trim()) return;
    setError(''); setAviso('');
    const cuerpo = { lista: activa, valor: nuevo.trim() };
    if (String(nuevoOrden).trim() !== '') cuerpo.orden = parseInt(nuevoOrden);
    try {
      await axios.post(`${API}/listas`, cuerpo);
      setNuevo(''); setNuevoOrden('');
      await cargar();
    } catch (e) { setError(msgError(e)); }
  };

  const guardarEdicion = async () => {
    if (!editando.valor.trim()) return;
    setError('');
    try {
      await axios.put(`${API}/listas?id=${editando.id}`, {
        lista: activa,
        valor: editando.valor.trim(),
        orden: parseInt(editando.orden) || 0
      });
      setEditando(null);
      await cargar();
    } catch (e) { setError(msgError(e)); }
  };

  const desactivar = async (v) => {
    if (!window.confirm(`Desactivar "${v.valor}"? Los expedientes que ya lo usan lo conservan.`)) return;
    try { await axios.delete(`${API}/listas?id=${v.id}`); await cargar(); }
    catch (e) { setError(msgError(e)); }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: '16px' }}>
      <div style={{ ...tarjeta, padding: '10px' }}>
        {LISTAS_RRHH.map(l => (
          <button key={l} onClick={() => { setActiva(l); setEditando(null); setNuevo(''); }}
            style={{
              display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', marginBottom: 2,
              border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13,
              background: activa === l ? 'rgba(135,183,73,0.15)' : 'transparent',
              color: activa === l ? '#012E40' : '#666',
              fontWeight: activa === l ? '600' : '500'
            }}>
            {NOMBRE_LISTA[l] || l}
            <span style={{ float: 'right', color: '#aaa', fontSize: 12 }}>{(todas[l] || []).length}</span>
          </button>
        ))}
      </div>

      <div style={{ ...tarjeta, padding: '20px' }}>
        <h3 style={{ margin: '0 0 6px', fontSize: '14px', fontWeight: '700', color: '#012E40' }}>
          {NOMBRE_LISTA[activa] || activa}
        </h3>
        {NOTA_LISTA[activa] && (
          <p style={{ fontSize: 12, color: '#8A6100', background: '#FDF4E0', border: '1px solid #f0e0b8', borderRadius: 8, padding: '8px 12px', marginTop: 0, marginBottom: 14 }}>
            {NOTA_LISTA[activa]}
          </p>
        )}

        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <input value={nuevo} onChange={e => setNuevo(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') agregar(); }}
            placeholder="Agregar valor..." style={inputStyle} />
          <input type="number" value={nuevoOrden} onChange={e => setNuevoOrden(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') agregar(); }}
            placeholder="Orden"
            title="Si escribís una posición ocupada, el que estaba ahí y los siguientes bajan uno. Vacío = queda de último."
            style={{ ...inputStyle, width: 90, flexShrink: 0 }} />
          <button onClick={agregar} style={btnPrimario}>Agregar</button>
        </div>

        {cargando ? <div style={{ padding: 30, textAlign: 'center', color: '#888' }}>Cargando...</div>
          : valores.length === 0 ? <div style={{ padding: 30, textAlign: 'center', color: '#bbb' }}>Esta lista esta vacia.</div>
          : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <tbody>
                {valores.map(v => (
                  <tr key={v.id} style={{ borderBottom: '1px solid #f3f3f3' }}>
                    <td style={{ ...tdStyle, width: 80, color: '#bbb' }}>
                      {editando?.id === v.id
                        ? <input type="number" value={editando.orden}
                            onChange={e => setEditando(p => ({ ...p, orden: e.target.value }))}
                            style={{ ...inputStyle, width: 70 }} />
                        : v.orden}
                    </td>
                    <td style={{ ...tdStyle, color: '#012E40' }}>
                      {editando?.id === v.id
                        ? <input value={editando.valor} onChange={e => setEditando(p => ({ ...p, valor: e.target.value }))} style={inputStyle} />
                        : v.valor}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {editando?.id === v.id ? (
                        <>
                          <button onClick={() => setEditando(null)} style={miniBtn}>Cancelar</button>
                          <button onClick={guardarEdicion} style={{ ...miniBtn, marginLeft: 6, background: '#012E40', color: '#fff', borderColor: '#012E40' }}>Guardar</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => setEditando({ id: v.id, valor: v.valor, orden: v.orden })} style={miniBtn}>Editar</button>
                          <button onClick={() => desactivar(v)} style={{ ...miniBtn, color: '#A32D2D', borderColor: '#fcc', marginLeft: 6 }}>Desactivar</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
    </div>
  );
}

// ── Cargos aprobadores (D6) — lectura RRHH, escritura RRHH + admin ───────
function Aprobadores({ setError, setAviso }) {
  const isAdmin = useAdmin();
  const [valores, setValores]   = useState({});
  const [form, setForm]         = useState({});
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await axios.get(`${API}/rrhh-catalogos?recurso=aprobadores`);
      const m = {};
      (r.data || []).forEach(x => { m[x.clave] = x.valor; });
      setValores(m);
      setForm(m);
    } catch (e) { setError(msgError(e)); }
    setCargando(false);
  }, [setError]);

  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async (clave) => {
    setGuardando(true); setError(''); setAviso('');
    try {
      await axios.put(`${API}/rrhh-catalogos?recurso=aprobadores`, { clave, oid: form[clave] || null });
      setAviso('Cargo aprobador actualizado.');
      await cargar();
    } catch (e) { setError(msgError(e)); }
    finally { setGuardando(false); }
  };

  const fila = (clave, titulo, descripcion) => (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#012E40' }}>{titulo}</div>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>{descripcion}</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input value={form[clave] || ''} disabled={!isAdmin}
          onChange={e => setForm(p => ({ ...p, [clave]: e.target.value }))}
          placeholder="oid de Azure AD (no el correo)"
          style={{ ...inputStyle, fontFamily: 'monospace', opacity: isAdmin ? 1 : 0.6 }} />
        {isAdmin && (
          <button onClick={() => guardar(clave)} disabled={guardando || form[clave] === valores[clave]}
            style={{ ...btnNavy, opacity: (guardando || form[clave] === valores[clave]) ? 0.5 : 1 }}>
            Guardar
          </button>
        )}
      </div>
    </div>
  );

  if (cargando) return <div style={{ ...tarjeta, padding: 30, textAlign: 'center', color: '#888' }}>Cargando...</div>;

  return (
    <div style={{ ...tarjeta, padding: '20px' }}>
      <h3 style={{ margin: '0 0 6px', fontSize: '14px', fontWeight: '700', color: '#012E40' }}>Cargos aprobadores</h3>
      <p style={{ fontSize: 12, color: '#888', marginTop: 0, marginBottom: 18, lineHeight: 1.5 }}>
        Definen quien aprueba cada paso de las acciones de personal. Se guarda el <b>oid</b> de la cuenta de
        Azure AD, no el correo: el correo cambia, el oid no. No dependen de la jefatura del colaborador (D6).
        Para que la omision por autoaprobacion funcione (D7), el expediente de esa persona debe tener su
        <b> azure_oid</b> registrado.
      </p>

      {!isAdmin && (
        <div style={{ background: '#FDF4E0', border: '1px solid #f0e0b8', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#8A6100', marginBottom: 18 }}>
          Solo lectura: cambiar los cargos aprobadores requiere ser administrador ademas de RRHH.
        </div>
      )}

      {fila('rrhh_aprobador_rrhh', 'Aprobador de RRHH', 'Primer paso de la doble aprobacion.')}
      {fila('rrhh_aprobador_gerencia', 'Aprobador de Gerencia', 'Paso final: al aprobar, el cambio se aplica al expediente.')}
    </div>
  );
}

const miniBtn = {
  padding: '4px 12px', background: 'none', border: '1px solid #ddd', borderRadius: '6px',
  fontSize: '12px', cursor: 'pointer', color: '#555'
};
